import React, { useState, memo } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { ArrowRight, CheckCircle, Copy, Check, MessageSquare, Smartphone, FileText, UserPlus, History, Zap, Brain, Share2 as ShareIcon, Quote, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
export function LandingPage() {
  const [copied, setCopied] = useState(false);
  const phoneNumber = '(774) 472-7423';
  const smsHref = `sms:+1${phoneNumber.replace(/\D/g, '')}?body=Hi%20ScribeTok!%20I%20want%20to%20transcribe%20a%20video.`;
  const handleCopyNumber = () => {
    navigator.clipboard.writeText(phoneNumber);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return <div className="min-h-screen w-full bg-background px-4 py-12 md:px-16 lg:px-32 flex flex-col items-center justify-center overflow-x-hidden" data-id="element-451">
      <div className="w-full max-w-7xl mx-auto px-4 md:px-6" data-id="element-452">
        {/* Logo */}
        <div className="flex justify-center mb-10" data-id="element-453">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/6tsscMEhsZ3L4JwT2r374e/ScribeTok-logo.png" alt="ScribeTok Logo" className="h-16 w-auto" data-id="element-454" />
        </div>

        {/* Hero */}
        <div className="text-center mb-12 animate-slide-down max-w-3xl mx-auto" style={{
        animationDelay: '100ms'
      }} data-id="element-455">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-5 leading-tight tracking-tight" data-id="element-456">
            Turn any TikTok into a reply{' '}
            <span className="text-primary" data-id="element-457">you'll actually send.</span>
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground mb-3 max-w-2xl mx-auto" data-id="element-458">
            Text a link. Get the transcript, best quote, key takeaways, and a
            draft response in under a minute.
          </p>
          <p className="text-sm text-muted-foreground mb-8" data-id="element-459">
            Works with TikTok, Reels, YouTube, and public videos.
          </p>

          {/* Mobile: full-width primary CTA */}
          <a href={smsHref} className="block md:hidden mb-4" data-id="element-460">
            <Button size="lg" className="w-full text-base" data-id="element-461">
              <MessageSquare size={18} className="mr-2 flex-shrink-0" data-id="element-462" />
              Open Messages
            </Button>
          </a>

          {/* Desktop: number copy + button side by side */}
          <div className="hidden md:flex items-center gap-4 justify-center mb-4" data-id="element-463">
            <div className="flex items-center gap-2 px-5 py-3 bg-secondary/70 rounded-full cursor-pointer hover:bg-secondary/90 transition-colors" onClick={handleCopyNumber} data-id="element-464">
              <span className="font-medium" data-id="element-465">{phoneNumber}</span>
              {copied ? <Check size={16} className="text-success" data-id="element-466" /> : <Copy size={16} data-id="element-467" />}
            </div>
            <a href={smsHref} data-id="element-468">
              <Button size="lg" className="text-base px-7" data-id="element-469">
                <MessageSquare size={18} className="mr-2 flex-shrink-0" data-id="element-470" />
                Open Messages
              </Button>
            </a>
          </div>

          {/* Desktop: QR as small convenience aside */}
          <div className="hidden md:flex flex-col items-center mb-6" data-id="element-471">
            <img src="https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=sms:+17744727423" alt="SMS QR Code" className="rounded-lg shadow-sm mb-1" width={120} height={120} data-id="element-472" />
            <p className="text-xs text-muted-foreground" data-id="element-473">Or scan to open SMS</p>
          </div>

          {/* Mobile: phone number visible but secondary */}
          <div className="flex md:hidden items-center gap-2 px-4 py-2 bg-secondary/70 rounded-full cursor-pointer mx-auto w-fit mb-6 hover:bg-secondary/90 transition-colors" onClick={handleCopyNumber} data-id="element-474">
            <span className="font-medium text-sm" data-id="element-475">{phoneNumber}</span>
            {copied ? <span className="flex items-center gap-1 text-success text-xs font-medium" data-id="element-476">
                <Check size={13} data-id="element-477" />
                Copied!
              </span> : <span className="flex items-center gap-1 text-muted-foreground text-xs" data-id="element-478">
                <Copy size={13} data-id="element-479" />
                Copy
              </span>}
          </div>

          {/* Trust strip */}
          <div className="flex flex-wrap gap-x-6 gap-y-2 justify-center text-sm text-muted-foreground" data-id="element-480">
            <span className="flex items-center gap-1.5" data-id="element-481">
              <CheckCircle size={14} className="text-success flex-shrink-0" data-id="element-482" />
              Public videos only
            </span>
            <span className="flex items-center gap-1.5" data-id="element-483">
              <CheckCircle size={14} className="text-success flex-shrink-0" data-id="element-484" />
              You text first — we don't spam
            </span>
            <span className="flex items-center gap-1.5" data-id="element-485">
              <CheckCircle size={14} className="text-success flex-shrink-0" data-id="element-486" />
              Reply STOP to opt out
            </span>
            <span className="flex items-center gap-1.5 text-muted-foreground/80" data-id="element-487">
              Msg &amp; data rates may apply
            </span>
          </div>
        </div>

        {/* Demo snippet — immediately below hero */}
        <div className="mb-16 animate-fade-in" style={{
        animationDelay: '250ms'
      }} data-id="element-488">
          <p className="text-center text-sm font-medium text-muted-foreground mb-4 uppercase tracking-wide" data-id="element-489">
            See it in action
          </p>
          <div className="max-w-sm mx-auto bg-secondary/20 rounded-2xl p-5 border border-border/20 space-y-3" data-id="element-490">
            <div className="flex justify-end" data-id="element-491">
              <div className="tiktok-gradient text-white px-4 py-2.5 rounded-2xl max-w-[220px] text-sm shadow-sm break-words" data-id="element-492">
                https://tiktok.com/@user/video/123
              </div>
            </div>
            <div className="flex justify-start" data-id="element-493">
              <div className="bg-foreground text-background px-4 py-2.5 rounded-2xl max-w-[220px] text-sm shadow-sm break-words" data-id="element-494">
                <span className="mr-1.5" data-id="element-495">✅</span> Transcript ready! "Today I'm
                showing three productivity hacks..."
              </div>
            </div>
            <div className="flex justify-end" data-id="element-496">
              <div className="tiktok-gradient text-white px-4 py-2.5 rounded-2xl max-w-[220px] text-sm shadow-sm break-words" data-id="element-497">
                Help me respond to this — my wife sent it
              </div>
            </div>
            <div className="flex justify-start" data-id="element-498">
              <div className="bg-foreground text-background px-4 py-2.5 rounded-2xl max-w-[220px] text-sm shadow-sm break-words" data-id="element-499">
                <span className="mr-1.5" data-id="element-500">💬</span> Here's a thoughtful response:
                "I watched this and it really made me think about..."
              </div>
            </div>
          </div>
          <div className="flex justify-center mt-4" data-id="element-501">
            <Link to="/app" className="text-sm text-muted-foreground hover:text-foreground transition-colors underline underline-offset-4" data-id="element-502">
              Browse public transcripts
            </Link>
          </div>
        </div>

        {/* NEW: More Than Just Transcripts Section */}
        <div className="mb-16 animate-fade-in" style={{
        animationDelay: '350ms'
      }} data-id="element-503">
          <div className="text-center mb-8" data-id="element-504">
            <h2 className="text-3xl md:text-4xl font-bold mb-3" data-id="element-505">
              More Than Just Transcripts
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto" data-id="element-506">
              Get instant insights, AI-powered chat, and shareable quotes from
              every video
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-7" data-id="element-507">
            {/* Card 1 - AI Chat (highlighted) */}
            <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-3xl p-8 border-2 border-primary/30 shadow-md hover:shadow-lg transition-all duration-300 hover:border-primary/50 group" data-id="element-508">
              <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mb-5 transition-transform group-hover:scale-110 duration-300" data-id="element-509">
                <MessageSquare size={28} className="text-primary" data-id="element-510" />
              </div>
              <h3 className="text-xl font-semibold mb-3" data-id="element-511">
                Chat with Your Transcript
              </h3>
              <p className="text-muted-foreground mb-4" data-id="element-512">
                "Help me draft a response to this relationship advice." Our AI
                understands the full context and helps you engage.
              </p>
              <div className="bg-background/50 border border-border/40 rounded-xl p-4 mt-4" data-id="element-513">
                <p className="text-sm italic text-muted-foreground" data-id="element-514">
                  "My wife sent me this TikTok about communication... help me
                  write a thoughtful response."
                </p>
              </div>
            </div>

            {/* Card 2 - Quote Extraction */}
            <div className="bg-secondary/10 rounded-3xl p-8 border border-border/20 shadow-sm hover:shadow-md transition-all duration-300 hover:border-border/40 group" data-id="element-515">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-5 transition-transform group-hover:scale-110 duration-300" data-id="element-516">
                <Quote size={28} className="text-primary" data-id="element-517" />
              </div>
              <h3 className="text-xl font-semibold mb-3" data-id="element-518">
                Best Quote Extraction
              </h3>
              <p className="text-muted-foreground mb-4" data-id="element-519">
                AI picks the most memorable, shareable moment from every video.
              </p>
              <div className="bg-secondary/30 rounded-xl p-3 mt-4 font-mono text-sm" data-id="element-520">
                <span className="text-primary" data-id="element-521">/quote</span> - Get just the best
                quote
              </div>
            </div>

            {/* Card 3 - TLDR */}
            <div className="bg-secondary/10 rounded-3xl p-8 border border-border/20 shadow-sm hover:shadow-md transition-all duration-300 hover:border-border/40 group" data-id="element-522">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-5 transition-transform group-hover:scale-110 duration-300" data-id="element-523">
                <Sparkles size={28} className="text-primary" data-id="element-524" />
              </div>
              <h3 className="text-xl font-semibold mb-3" data-id="element-525">Instant TLDR</h3>
              <p className="text-muted-foreground mb-4" data-id="element-526">
                3-5 bullet points capturing the key takeaways. Skip the fluff.
              </p>
              <div className="bg-secondary/30 rounded-xl p-3 mt-4 font-mono text-sm" data-id="element-527">
                <span className="text-primary" data-id="element-528">/tldr</span> - Key points only
              </div>
            </div>
          </div>
        </div>

        {/* Features Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-7 mb-16" data-id="element-529">
          {/* How It Works - Spans 8 columns on larger screens */}
          <div className="md:col-span-8 bg-secondary/10 rounded-3xl p-8 animate-fade-in border border-border/20 shadow-sm hover:shadow-md transition-all duration-300 hover:border-border/40 group" style={{
          animationDelay: '500ms'
        }} data-id="element-530">
            <h3 className="text-2xl font-medium mb-6 group-hover:text-primary transition-colors" data-id="element-531">
              How It Works
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6" data-id="element-532">
              <div className="flex flex-col items-center text-center p-4 bg-background/50 rounded-2xl hover:bg-background/80 transition-colors" data-id="element-533">
                <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mb-4 transition-transform hover:scale-110 duration-300" data-id="element-534">
                  <MessageSquare size={24} className="text-primary" data-id="element-535" />
                </div>
                <h3 className="text-lg font-medium mb-2" data-id="element-536">
                  Step 1: Text a video link
                </h3>
                <p className="text-sm text-muted-foreground" data-id="element-537">
                  Send any TikTok URL to {phoneNumber}
                </p>
              </div>
              <div className="flex flex-col items-center text-center p-4 bg-background/50 rounded-2xl hover:bg-background/80 transition-colors" data-id="element-538">
                <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mb-4 transition-transform hover:scale-110 duration-300" data-id="element-539">
                  <FileText size={24} className="text-primary" data-id="element-540" />
                </div>
                <h3 className="text-lg font-medium mb-2" data-id="element-541">
                  Step 2: Wait a moment
                </h3>
                <p className="text-sm text-muted-foreground" data-id="element-542">
                  Our AI extracts the text quickly
                </p>
              </div>
              <div className="flex flex-col items-center text-center p-4 bg-background/50 rounded-2xl hover:bg-background/80 transition-colors" data-id="element-543">
                <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mb-4 transition-transform hover:scale-110 duration-300" data-id="element-544">
                  <ShareIcon size={24} className="text-primary" data-id="element-545" />
                </div>
                <h3 className="text-lg font-medium mb-2" data-id="element-546">
                  Step 3: Get your transcript
                </h3>
                <p className="text-sm text-muted-foreground" data-id="element-547">
                  Receive text with a shareable link
                </p>
              </div>
            </div>
          </div>

          {/* Key Benefits - Takes 4 columns */}
          <div className="md:col-span-4 bg-primary/5 rounded-3xl p-8 animate-fade-in border border-primary/10 shadow-sm hover:shadow-md transition-all duration-300 hover:border-primary/20 group" style={{
          animationDelay: '600ms'
        }} data-id="element-548">
            <h3 className="text-2xl font-medium mb-6 flex items-center group-hover:text-primary transition-colors" data-id="element-549">
              <Zap size={20} className="text-primary mr-3" data-id="element-550" />
              Key Benefits
            </h3>
            <div className="space-y-5" data-id="element-551">
              <div className="flex items-start gap-4 p-3 bg-background/50 rounded-2xl hover:bg-background/80 transition-colors" data-id="element-552">
                <History size={20} className="text-primary mt-1 flex-shrink-0" data-id="element-553" />
                <div data-id="element-554">
                  <p className="font-medium text-base" data-id="element-555">No Account Needed</p>
                  <p className="text-sm text-muted-foreground" data-id="element-556">
                    Transcripts saved to your phone number
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-3 bg-background/50 rounded-2xl hover:bg-background/80 transition-colors" data-id="element-557">
                <UserPlus size={20} className="text-primary mt-1 flex-shrink-0" data-id="element-558" />
                <div data-id="element-559">
                  <p className="font-medium text-base" data-id="element-560">Create Account Later</p>
                  <p className="text-sm text-muted-foreground" data-id="element-561">
                    Text <span className="font-medium" data-id="element-562">/register</span> anytime
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-3 bg-background/50 rounded-2xl hover:bg-background/80 transition-colors" data-id="element-563">
                <Brain size={20} className="text-primary mt-1 flex-shrink-0" data-id="element-564" />
                <div data-id="element-565">
                  <p className="font-medium text-base" data-id="element-566">Powerful Commands</p>
                  <p className="text-sm text-muted-foreground" data-id="element-567">
                    Use <span className="font-medium" data-id="element-568">/tldr</span> for key
                    takeaways
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Trust and Commands Bento Grid - Updated with new commands */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-7 mb-16" data-id="element-569">
          {/* Trust Indicators - Takes 4 columns */}
          <div className="md:col-span-4 flex flex-col justify-center bg-secondary/10 rounded-3xl p-8 animate-fade-in border border-border/20 shadow-sm hover:shadow-md transition-all duration-300 hover:border-border/40" style={{
          animationDelay: '700ms'
        }} data-id="element-570">
            <h3 className="text-2xl font-medium mb-6" data-id="element-571">Why Trust Us</h3>
            <div className="space-y-4" data-id="element-572">
              <div className="flex items-center gap-3 p-3 bg-background/50 rounded-2xl hover:bg-background/80 transition-colors" data-id="element-573">
                <CheckCircle size={22} className="text-success" data-id="element-574" />
                <span className="text-base font-medium" data-id="element-575">Fast & Accurate</span>
              </div>
              <div className="flex items-center gap-3 p-3 bg-background/50 rounded-2xl hover:bg-background/80 transition-colors" data-id="element-576">
                <CheckCircle size={22} className="text-success" data-id="element-577" />
                <span className="text-base font-medium" data-id="element-578">
                  Multiple Languages
                </span>
              </div>
              <div className="flex items-center gap-3 p-3 bg-background/50 rounded-2xl hover:bg-background/80 transition-colors" data-id="element-579">
                <CheckCircle size={22} className="text-success" data-id="element-580" />
                <span className="text-base font-medium" data-id="element-581">
                  Public Videos Only
                </span>
              </div>
            </div>
          </div>

          {/* SMS Commands - Takes 8 columns - Updated with new commands */}
          <div className="md:col-span-8 rounded-3xl overflow-hidden animate-fade-in" style={{
          animationDelay: '800ms'
        }} data-id="element-582">
            <div className="bg-secondary/10 p-8 rounded-3xl border border-border/20 shadow-sm hover:shadow-md transition-all duration-300 hover:border-border/40" data-id="element-583">
              <h3 className="text-2xl font-medium mb-6" data-id="element-584">SMS Commands</h3>
              <div className="grid grid-cols-2 gap-4" data-id="element-585">
                <div className="bg-secondary/30 p-4 rounded-xl hover:bg-secondary/50 transition-colors hover:scale-105 transform duration-300 border border-border/20" data-id="element-586">
                  <p className="font-medium text-base" data-id="element-587">/help</p>
                  <p className="text-sm text-muted-foreground" data-id="element-588">
                    List all commands
                  </p>
                </div>
                <div className="bg-secondary/30 p-4 rounded-xl hover:bg-secondary/50 transition-colors hover:scale-105 transform duration-300 border border-border/20" data-id="element-589">
                  <p className="font-medium text-base" data-id="element-590">/register</p>
                  <p className="text-sm text-muted-foreground" data-id="element-591">
                    Create account
                  </p>
                </div>
                <div className="bg-primary/10 p-4 rounded-xl hover:bg-primary/20 transition-colors hover:scale-105 transform duration-300 border border-primary/20" data-id="element-592">
                  <p className="font-medium text-base flex items-center gap-2" data-id="element-593">
                    <MessageSquare size={16} className="text-primary" data-id="element-594" />
                    /chat
                  </p>
                  <p className="text-sm text-muted-foreground" data-id="element-595">
                    Ask questions about transcript
                  </p>
                </div>
                <div className="bg-primary/10 p-4 rounded-xl hover:bg-primary/20 transition-colors hover:scale-105 transform duration-300 border border-primary/20" data-id="element-596">
                  <p className="font-medium text-base flex items-center gap-2" data-id="element-597">
                    <Quote size={16} className="text-primary" data-id="element-598" />
                    /quote
                  </p>
                  <p className="text-sm text-muted-foreground" data-id="element-599">
                    Get best quote
                  </p>
                </div>
                <div className="bg-primary/10 p-4 rounded-xl hover:bg-primary/20 transition-colors hover:scale-105 transform duration-300 border border-primary/20" data-id="element-600">
                  <p className="font-medium text-base flex items-center gap-2" data-id="element-601">
                    <Sparkles size={16} className="text-primary" data-id="element-602" />
                    /tldr
                  </p>
                  <p className="text-sm text-muted-foreground" data-id="element-603">Key takeaways</p>
                </div>
                <div className="bg-secondary/30 p-4 rounded-xl hover:bg-secondary/50 transition-colors hover:scale-105 transform duration-300 border border-border/20" data-id="element-604">
                  <p className="font-medium text-base" data-id="element-605">/summary</p>
                  <p className="text-sm text-muted-foreground" data-id="element-606">
                    Get quick insights
                  </p>
                </div>
                <div className="bg-secondary/30 p-4 rounded-xl hover:bg-secondary/50 transition-colors hover:scale-105 transform duration-300 border border-border/20" data-id="element-607">
                  <p className="font-medium text-base" data-id="element-608">/vault</p>
                  <p className="text-sm text-muted-foreground" data-id="element-609">
                    View recent transcripts
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Full Conversation Example - Updated with AI Chat */}
        <div className="mb-16 animate-fade-in" style={{
        animationDelay: '900ms'
      }} data-id="element-610">
          <div className="bg-secondary/10 rounded-3xl p-6 md:p-8 border border-border/20 shadow-sm hover:shadow-md transition-all duration-300 hover:border-border/40" data-id="element-611">
            <div className="flex items-center justify-between mb-5" data-id="element-612">
              <h3 className="text-2xl font-medium" data-id="element-613">
                Complete Conversation Flow
              </h3>
              <div className="bg-primary/10 text-primary px-3 py-1 rounded-full text-xs font-medium" data-id="element-614">
                Real-time Experience
              </div>
            </div>
            <div className="max-w-2xl mx-auto bg-background/50 p-5 rounded-2xl" data-id="element-615">
              <div className="space-y-4" data-id="element-616">
                {/* First message */}
                <div className="flex justify-end" data-id="element-617">
                  <div className="tiktok-gradient text-white px-4 py-3 rounded-2xl max-w-[280px] shadow-sm break-words hover:scale-105 transition-transform" data-id="element-618">
                    https://www.tiktok.com/@username/video/1234567890
                  </div>
                </div>
                {/* Response messages */}
                <div className="flex justify-start" data-id="element-619">
                  <div className="bg-foreground text-background px-4 py-3 rounded-2xl max-w-[280px] shadow-sm break-words hover:scale-105 transition-transform" data-id="element-620">
                    <p className="flex items-start gap-2" data-id="element-621">
                      <span className="text-xl flex-shrink-0" data-id="element-622">🎬</span>
                      <span data-id="element-623">
                        Thanks for using ScribeTok! You'll receive a transcript
                        shortly.
                      </span>
                    </p>
                  </div>
                </div>
                <div className="flex justify-start" data-id="element-624">
                  <div className="bg-foreground text-background px-4 py-3 rounded-2xl max-w-[280px] shadow-sm break-words hover:scale-105 transition-transform" data-id="element-625">
                    <p className="flex items-start gap-2" data-id="element-626">
                      <span className="text-xl flex-shrink-0" data-id="element-627">⏳</span>
                      <span data-id="element-628">Got your link! Transcribing now...</span>
                    </p>
                  </div>
                </div>
                {/* Transcript result */}
                <div className="flex justify-start" data-id="element-629">
                  <div className="bg-foreground text-background px-4 py-3 rounded-2xl max-w-[350px] shadow-sm break-words hover:scale-105 transition-transform" data-id="element-630">
                    <p className="flex items-start gap-2" data-id="element-631">
                      <span className="text-xl flex-shrink-0" data-id="element-632">✅</span>
                      <span data-id="element-633">Transcript ready!</span>
                    </p>
                    <p className="mt-3 ml-7" data-id="element-634">
                      "Hey everyone, today I'm going to show you three
                      productivity hacks that changed my life..."
                    </p>
                    <p className="text-xs mt-3 text-primary font-medium ml-7" data-id="element-635">
                      share.scribetok.com/v/b70c860b-d7dc
                    </p>
                  </div>
                </div>
                {/* AI Chat request */}
                <div className="flex justify-end" data-id="element-636">
                  <div className="tiktok-gradient text-white px-4 py-3 rounded-2xl max-w-[280px] shadow-sm break-words hover:scale-105 transition-transform" data-id="element-637">
                    Help me respond to this - my wife sent it to me
                  </div>
                </div>
                {/* AI Chat response */}
                <div className="flex justify-start" data-id="element-638">
                  <div className="bg-foreground text-background px-4 py-3 rounded-2xl max-w-[350px] shadow-sm break-words hover:scale-105 transition-transform" data-id="element-639">
                    <p className="flex items-start gap-2" data-id="element-640">
                      <span className="text-xl flex-shrink-0" data-id="element-641">💬</span>
                      <span className="font-medium" data-id="element-642">
                        Here's a thoughtful response:
                      </span>
                    </p>
                    <p className="mt-3 ml-7 text-sm" data-id="element-643">
                      "I watched this and it really made me think about how we
                      can improve our daily routines together. The time blocking
                      idea especially resonated with me..."
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-center mt-6" data-id="element-644">
              <div className="flex items-center gap-3 text-sm text-muted-foreground bg-secondary/30 px-4 py-2 rounded-full" data-id="element-645">
                <span className="text-primary" data-id="element-646">🔄</span>
                <span data-id="element-647">Experience this yourself in seconds</span>
              </div>
            </div>
          </div>
        </div>

        {/* Compliance Footer */}
        <div className="mt-8 border-t border-border/30 pt-6 text-sm text-muted-foreground" data-id="element-648">
          <p data-id="element-649">
            ScribeTok is a user-initiated SMS service. We do not send
            promotional messages. You can opt out at any time by replying STOP.
            Msg & data rates may apply.
          </p>
          <p className="mt-3" data-id="element-650">
            <Link to="/text/compliance" className="text-primary hover:underline" data-id="element-651">
              View full terms
            </Link>
          </p>
        </div>
      </div>
    </div>;
}